import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Container,
  Button,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';

const CarouselSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  // Sample carousel items - using your existing data
  const carouselItems = [
    {
      id: 1,
      title: "Welcome to NYCS Surat",
      description: "Empowering Youth, Enabling Futures.",
      image: "/Dashboard/slide-1.jpg",
      buttonText: "Learn More",
      link: "/about/story/"
    },
    {
      id: 2,
      title: "Are you looking for every solution on one platform?",
      description: `Ie...jobs, business ideas, finance, part-time income, advises on IPR, projects,Knowledge Partnership  
      NYCS Surat is the platform you are looking for...`,
      image: "/savings-1.jpg",
      buttonText: "Join NYCS Surat",
      link: "/FormApplication"
    },

    {
      id: 3,
      title: "Become a member of our society",
      description: "Apply for the membership",
      image: "/Yuva-5.png",
      buttonText: "Join Now",
      link: "/FormApplication"
    }
  ];

  // Auto-play functionality
  useEffect(() => {
    let interval;
    if (isAutoPlaying) {
      interval = setInterval(() => {
        setCurrentSlide((prev) =>
          prev === carouselItems.length - 1 ? 0 : prev + 1
        );
      }, 5000); // Change slide every 5 seconds
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying, carouselItems.length]);

  // Navigation handlers
  const handleNext = () => {
    setCurrentSlide((prev) =>
      prev === carouselItems.length - 1 ? 0 : prev + 1
    );
  };

  const handlePrev = () => {
    setCurrentSlide((prev) =>
      prev === 0 ? carouselItems.length - 1 : prev - 1
    );
  };

  const handleDotClick = (index) => {
    setCurrentSlide(index);
  };

  // Pause auto-play on hover
  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => setIsAutoPlaying(true);

  // Animation variants
  const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2
      }
    },
    exit: {
      opacity: 0,
      y: -30,
      transition: { duration: 0.3 }
    }
  };

  const buttonVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { delay: 0.6, duration: 0.3 }
    },
    exit: { opacity: 0, transition: { duration: 0.2 } }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: isMobile ? '400px' : '800px',
        overflow: 'hidden',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Slides */}
      {carouselItems.map((item, index) => (
        <Box
          key={item.id}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: currentSlide === index ? 1 : 0,
            transition: 'opacity 1s ease',
            zIndex: currentSlide === index ? 1 : 0,
          }}
        >
          {/* Background Image */}
          <Box
            component="img"
            src={item.image}
            alt={item.title}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />

          {/* Dark Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
            }}
          />

          {/* Content */}
          {currentSlide === index && (
            <Container
              maxWidth="lg"
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'white',
                textAlign: 'left',
                zIndex: 5,
              }}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={`content-${index}`}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={textVariants}
                >
                  <motion.div variants={textVariants}>
                    <Typography
                      variant={isMobile ? 'h6' : 'h2'}
                      component="h2"
                      sx={{
                        mb: 2,
                        fontWeight: 700,
                        textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                      }}
                    >
                      <Box component="span" sx={{ color: 'common.white' }}>
                        {/* {item.title.split(' ').slice(0, -1).join(' ')}{' '} */}
                        {item.title}
                      </Box>
                      {/* <Box
                        component="span"
                        sx={{
                          background: 'linear-gradient(to right, #1976d2, #512da8)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          textShadow: 'none'
                        }}
                      >
                        {item.title.split(' ').slice(-1)}
                      </Box> */}
                    </Typography>
                  </motion.div>

                  <motion.div variants={textVariants}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 4,
                        maxWidth: '800px',
                        mx: 'left',
                        textShadow: '1px 1px 3px rgba(0,0,0,0.5)',
                      }}
                    >
                      {item.description}
                    </Typography>
                  </motion.div>


                  <motion.div variants={buttonVariants}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        maxHeight:"50px",
                        gap:5 
                      }}
                    >
                      {item.id === 3 ? <motion.div >
                        <Typography
                          variant="h3"
                          sx={{
                            mb: 4,
                            maxWidth: '800px',
                            mx: 'left',
                            textShadow: '1px 1px 3px rgba(0,0,0,0.5)',
                          }}
                        >
                          Why join NYCS?
                        </Typography>
                      </motion.div> : null}
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate(item.link)}
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                          px: 4,
                          py: 1.5,
                          background: 'linear-gradient(to right, #1976d2, #512da8)',
                          '&:hover': {
                            background: 'linear-gradient(to right, #1565c0, #4527a0)',
                            transform: 'translateY(-3px)',
                            boxShadow: theme.shadows[8],
                          },
                          transition: 'all 0.3s ease',
                          borderRadius: '50px',
                          fontWeight: 600,
                        }}
                      >
                        {item.buttonText}
                      </Button>
                    </Box>

                  </motion.div>
                </motion.div>
              </AnimatePresence>
            </Container>
          )}
        </Box>
      ))}

      {/* Navigation Arrows */}
      <IconButton
        onClick={handlePrev}
        sx={{
          position: 'absolute',
          left: { xs: 10, md: 30 },
          top: '50%',
          transform: 'translateY(-50%)',
          bgcolor: alpha(theme.palette.common.white, 0.3),
          color: 'common.white',
          zIndex: 10,
          '&:hover': {
            bgcolor: alpha(theme.palette.common.white, 0.5),
          },
          width: { xs: 40, md: 50 },
          height: { xs: 40, md: 50 },
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      <IconButton
        onClick={handleNext}
        sx={{
          position: 'absolute',
          right: { xs: 10, md: 30 },
          top: '50%',
          transform: 'translateY(-50%)',
          bgcolor: alpha(theme.palette.common.white, 0.3),
          color: 'common.white',
          zIndex: 10,
          '&:hover': {
            bgcolor: alpha(theme.palette.common.white, 0.5),
          },
          width: { xs: 40, md: 50 },
          height: { xs: 40, md: 50 },
        }}
      >
        <ArrowForwardIcon />
      </IconButton>

      {/* Indicator Dots */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 1,
          zIndex: 10,
        }}
      >
        {carouselItems.map((_, index) => (
          <IconButton
            key={index}
            onClick={() => handleDotClick(index)}
            sx={{
              p: 0.5,
              color: currentSlide === index
                ? 'primary.main'
                : alpha(theme.palette.common.white, 0.7),
              '&:hover': {
                color: currentSlide === index
                  ? 'primary.main'
                  : theme.palette.common.white,
              },
            }}
          >
            <FiberManualRecordIcon
              fontSize="small"
              sx={{
                color: currentSlide === index
                  ? 'primary.main'
                  : alpha(theme.palette.common.white, 0.7),
              }}
            />
          </IconButton>
        ))}
      </Box>
    </Box>
  );


};

export default CarouselSlider;